// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import {
    executeAwaitableHandlerChain,
    makeHandlerChainAwaitable,
    replaceInChain,
} from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import {
    getSearchDataModeActive,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadProducts from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import displayProducts, {
    chain,
    GetUnfilteredProducts,
    LoadRealTimeInventory,
    LoadRealTimePrices,
    PopulateApiParameter,
    RemoveNotAvailableAttributeValues,
} from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import { RealTimePricingModel } from "@insite/client-framework/Types/ApiModels";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import qs from "qs";

export const XngPopulateApiParameter: any = (props: any) => {
    const filters = props.result.productFilters;
    const {
        query,
        brandIds,
        productLineIds,
        priceFilters,
        attributeValueIds,
        searchWithinQueries,
        pageCategoryId,
        pageBrandId,
        pageProductLineId,
        ...apiParameter
    } = filters;
    props.apiParameter = {
        ...apiParameter,
        search: query,
        categoryId: filters.categoryId || filters.pageCategoryId,
        searchWithin: searchWithinQueries?.join(" "),
        includeSuggestions: filters.includeSuggestions !== false,
        brandIds: filters.pageBrandId ? [filters.pageBrandId] : brandIds,
        productLineIds: filters.pageProductLineId ? [filters.pageProductLineId] : productLineIds,
        priceFilters,
        attributeValueIds,
        expand: props.expandTokens || [],
        applyPersonalization: true,
        includeAttributes: ["includeOnProduct"],
        relevancy: props.parameter.relevancy,
        // Xng-Change
        includeProductsInSubCategories: !!(
            filters.categoryId?.length ||
            filters.pageCategoryId?.length ||
            props.result.catelogPage?.categoryId.length
        ),
    };
};

// Dont delete the categoryId in case we have include products for subCategories to fetch all the categoryFacets with the call
export const XngGetUnfilteredProducts: any = async (props: any) => {
    const unfilteredApiParameter = { ...props.apiParameter };
    if (unfilteredApiParameter.includeProductsInSubCategories) {
        delete unfilteredApiParameter.searchWithin;
        delete unfilteredApiParameter.brandIds;
        delete unfilteredApiParameter.productLineIds;
        delete unfilteredApiParameter.priceFilters;
        delete unfilteredApiParameter.attributeValueIds;
        unfilteredApiParameter.stockedItemsOnly = false;
    } else {
        delete unfilteredApiParameter.searchWithin;
        delete unfilteredApiParameter.brandIds;
        delete unfilteredApiParameter.productLineIds;
        delete unfilteredApiParameter.priceFilters;
        delete unfilteredApiParameter.attributeValueIds;
        delete unfilteredApiParameter.categoryId;
        unfilteredApiParameter.stockedItemsOnly = false;
    }

    props.result.unfilteredApiParameter = unfilteredApiParameter;

    const unfilteredProducts = getProductsDataView(props.getState(), unfilteredApiParameter).value;

    if (unfilteredProducts) {
        return;
    }

    await executeAwaitableHandlerChain(loadProducts, unfilteredApiParameter, props);
};

export const XngLoadRealTimePrices: any = async (props: any) => {
    const {
        result: { productInfosByProductId },
    } = props;
    const productIds = Object.keys(productInfosByProductId).filter(o => !productInfosByProductId[o]?.pricing);
    if (productIds.length === 0) {
        return;
    }

    if (
        getSettingsCollection(props.getState()).customSettings &&
        (getSettingsCollection(props.getState()).customSettings as any).postalCodeLocationFinder.toLowerCase() ===
            "true" &&
        typeof window !== "undefined" &&
        !window.localStorage.getItem("postalCodeID")?.length &&
        !props.getState().context.session.isAuthenticated
    ) {
        return;
    }

    const loadRealTimePricingParameter: Parameters<typeof loadRealTimePricing>[0] = {
        productPriceParameters: productIds.map(o => productInfosByProductId[o]!),
        onSuccess: realTimePricing => {
            props.dispatch({
                type: "Pages/ProductList/CompleteLoadRealTimePricing",
                realTimePricing,
            });
            props.pricingLoaded = true;
        },
        onError: () => {
            props.dispatch({
                type: "Pages/ProductList/FailedLoadRealTimePricing",
            });
            props.pricingLoaded = true;
        },
        onComplete(realTimePricingProps) {
            if (realTimePricingProps.apiResult) {
                this.onSuccess?.(realTimePricingProps.apiResult);
            } else if (realTimePricingProps.error) {
                this.onError?.(realTimePricingProps.error);
            }
        },
    };

    const settings = getSettingsCollection(props.getState());

    if (IS_SERVER_SIDE && getIsWebCrawler() && !settings.websiteSettings.includeRealTimeInventoryAndPricingForSeo) {
        const awaitableLoadRealTimePricing = makeHandlerChainAwaitable<
            Parameters<typeof loadRealTimePricing>[0],
            RealTimePricingModel
        >(loadRealTimePricing);
        const realTimePricing = await awaitableLoadRealTimePricing({
            productPriceParameters: loadRealTimePricingParameter.productPriceParameters,
        })(props.dispatch, props.getState);
        if (realTimePricing) {
            loadRealTimePricingParameter.onSuccess?.(realTimePricing);
        } else {
            loadRealTimePricingParameter.onError?.(realTimePricing);
        }
    } else {
        props.dispatch(loadRealTimePricing(loadRealTimePricingParameter));
    }

    const productSettings = settings.productSettings;
    if (productSettings.canSeePrices && productSettings.inventoryIncludedWithPricing) {
        await waitFor(() => !!props.pricingLoaded);
    }
};

export const XngLoadRealTimeInventory: any = (props: any) => {
    if (
        !props.result.products?.length ||
        (getIsWebCrawler() &&
            !getSettingsCollection(props.getState()).websiteSettings.includeRealTimeInventoryAndPricingForSeo)
    ) {
        return;
    }

    const {
        result: { productInfosByProductId },
    } = props;

    const productIds = Object.keys(productInfosByProductId).filter(o => !productInfosByProductId[o]?.inventory);
    if (productIds.length === 0) {
        return;
    }

    if (
        getSettingsCollection(props.getState()).customSettings &&
        (getSettingsCollection(props.getState()).customSettings as any).postalCodeLocationFinder.toLowerCase() ===
            "true" &&
        typeof window !== "undefined" &&
        !window.localStorage.getItem("postalCodeID")?.length &&
        !props.getState().context.session.isAuthenticated
    ) {
        return;
    }

    props.dispatch(
        loadRealTimeInventory({
            productIds,
            onSuccess: realTimeInventory => {
                props.dispatch({
                    type: "Pages/ProductList/CompleteLoadRealTimeInventory",
                    realTimeInventory,
                });
            },
            onError: () => {
                props.dispatch({
                    type: "Pages/ProductList/FailedLoadRealTimeInventory",
                });
            },
            onComplete(realTimeInventoryProps) {
                if (realTimeInventoryProps.apiResult) {
                    this.onSuccess?.(realTimeInventoryProps.apiResult);
                } else if (realTimeInventoryProps.error) {
                    this.onError?.(realTimeInventoryProps.error);
                }
            },
        }),
    );
};

export const XngRemoveNotAvailableAttributeValues: any = (props: any) => {
    // XNG-Change: get headerShopByCatLinks
    const state = props.getState();
    const headerShopByCatLinks =
        (getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "ShopByCategoryL1L2/ShopByCategoryL1L2";
        })?.fields.l1Links as any[]) ?? [];

    const productsDataView = getProductsDataView(props.getState(), props.apiParameter);
    if (!productsDataView.value) {
        return;
    }

    const possibleAttributeValueIds = new Set();
    productsDataView?.attributeTypeFacets?.forEach(a =>
        a.attributeValueFacets?.forEach(v => possibleAttributeValueIds.add(v.attributeValueId)),
    );
    // XNG-Change: add custom attributes to possibleAttributeValueIds
    headerShopByCatLinks.length &&
        headerShopByCatLinks.forEach(catLink => {
            const { type, value } = catLink.fields.destination;
            if (type?.toLowerCase() === "url" && value?.includes("attributeValueIds=")) {
                possibleAttributeValueIds.add(value.slice(value.indexOf("attributeValueIds=") + 18));
            }
        });
    const removedAttributeValueIds = props.result.productFilters.attributeValueIds?.filter(
        (o: any) => !possibleAttributeValueIds.has(o),
    );
    if (
        removedAttributeValueIds?.length &&
        !props.result.productFilters.stockedItemsOnly &&
        !props.result.productFilters.previouslyPurchasedProducts
    ) {
        const attributeValueIds = props.result.productFilters.attributeValueIds?.filter((o: any) =>
            possibleAttributeValueIds.has(o),
        );
        props.dispatch(removeProductFilters({ attributeValueIds: removedAttributeValueIds }));
        const parsedQuery = parseQueryString<any>(props.parameter.queryString);
        parsedQuery.attributeValueIds = attributeValueIds?.length ? attributeValueIds.join(",") : undefined;
        const parameter = { ...props.parameter, queryString: qs.stringify(parsedQuery) };
        props.dispatch(displayProducts(parameter));
        return false;
    }
};

replaceInChain(chain, PopulateApiParameter, XngPopulateApiParameter);
replaceInChain(chain, GetUnfilteredProducts, XngGetUnfilteredProducts);
replaceInChain(chain, LoadRealTimePrices, XngLoadRealTimePrices);
replaceInChain(chain, LoadRealTimeInventory, XngLoadRealTimeInventory);
replaceInChain(chain, RemoveNotAvailableAttributeValues, XngRemoveNotAvailableAttributeValues);
